/**
 * First-run setup wizard (spec §13.4). Twelve steps:
 * credential → language → industry → company → address → branding → admin →
 * localization → VAT → bank/QR-bill → numbering → review.
 *
 * Submits the frozen POST /setup/complete payload and ends authenticated.
 * The company logo is intentionally not collected here (the payload has no
 * logo field) — the wizard notes it can be uploaded later in settings.
 */
import { AuthLayout } from "../components/layout/AuthLayout.js";
import { Button } from "../components/ui/Button.js";
import { SetupContext, STEP_KEYS, useSetupWizard } from "./setup/wizard.js";
import {
  CompanyStep,
  CredentialStep,
  IndustryStep,
  LanguageStep,
} from "./setup/steps-start.js";
import {
  AddressStep,
  LocalizationStep,
  SystemAdminStep,
} from "./setup/steps-details.js";
import {
  BankStep,
  NumberingStep,
  ReviewStep,
  VatStep,
} from "./setup/steps-billing.js";

const STEPS = [
  CredentialStep,
  LanguageStep,
  IndustryStep,
  CompanyStep,
  AddressStep,
  SystemAdminStep,
  LocalizationStep,
  VatStep,
  BankStep,
  NumberingStep,
  ReviewStep,
];

export function SetupPage() {
  const wizard = useSetupWizard();
  const {
    t,
    isSubmitting,
    credential,
    validating,
    goNext,
    onSubmit,
    step,
    setStep,
    stepTitle,
  } = wizard;
  const CurrentStep = STEPS[step] ?? CredentialStep;
  return (
    <AuthLayout title={t("setup.title")} subtitle={t("setup.subtitle")} wide>
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-app-text">{stepTitle}</span>
          <span className="text-app-muted">
            {t("common.stepOf", {
              current: step + 1,
              total: STEP_KEYS.length,
            })}
          </span>
        </div>
        <div
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--p-content-hover-background)]"
          role="progressbar"
          aria-valuenow={step + 1}
          aria-valuemin={1}
          aria-valuemax={STEP_KEYS.length}
        >
          <div
            className="h-full rounded-full bg-brand transition-all"
            style={{
              width: `${((step + 1) / STEP_KEYS.length) * 100}%`,
            }}
          />
        </div>
      </div>

      <form onSubmit={(event) => event.preventDefault()} noValidate>
        <SetupContext.Provider value={wizard}>
          <CurrentStep />
        </SetupContext.Provider>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            disabled={step === 0 || isSubmitting}
            onClick={() => setStep((current) => Math.max(current - 1, 0))}
          >
            {t("common.back")}
          </Button>
          {step < STEP_KEYS.length - 1 ? (
            <Button
              type="button"
              disabled={(step === 0 && !credential) || validating}
              onClick={() => void goNext()}
            >
              {t("common.next")}
            </Button>
          ) : (
            <Button
              type="button"
              loading={isSubmitting || undefined}
              onClick={() => void onSubmit()}
            >
              {isSubmitting
                ? t("setup.review.completing")
                : t("setup.review.complete")}
            </Button>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}
